/**
 * MarketplaceDetail -- Single marketplace item page (marketplace.example.com/:locale/:slug)
 * Hero, action bar, tabs (overview/features/installation/requirements), sidebar with related items
 * Uses unified CMS API
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';

import { T } from '../styles/tokens';
import { Button } from '../components/ui';
const API_BASE = '/api/v1/cms';

interface MarketplaceEntry {
  id: string; slug: string; content_type_id: string; category_id: string;
  status: string; is_featured: number; is_popular: number;
  view_count: number; like_count: number;
  tags: string; metadata: string; published_at: string;
  title: string; excerpt: string; body: string;
  features_json: string; custom_fields: string;
  meta_title: string; meta_description: string;
  category_slug: string; category_color: string; category_icon: string; category_name: string;
}

interface RelatedEntry {
  id: string; slug: string; title: string; excerpt: string;
  metadata: string; category_color: string; category_name: string;
}

function getLocale(): string {
  const path = window.location.pathname.split('/')[1];
  if (path && (path === 'pt-BR' || path === 'en' || path === 'es')) return path;
  return 'pt-BR';
}

function parseMeta(m: string): Record<string, any> {
  try { return JSON.parse(m || '{}'); } catch { return {}; }
}

function parseCustom(c: string): Record<string, any> {
  try { return JSON.parse(c || '{}'); } catch { return {}; }
}

function renderSimpleMarkdown(md: string): string {
  if (!md) return '';
  return md
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_: string, _lang: string, code: string) => `<pre style="background:T.bgCard;padding:16px;border-radius:8px;overflow-x:auto;font-size:13px"><code>${code.replace(/</g, '&lt;')}</code></pre>`)
    .replace(/`([^`]+)`/g, '<code style="background:T.border;padding:2px 6px;border-radius:4px;font-size:13px">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/^### (.+)$/gm, `<h3 style="font-size:16px;font-weight:700;margin:24px 0 8px;color:${T.text}">$1</h3>`)
    .replace(/^## (.+)$/gm, `<h2 style="font-size:18px;font-weight:700;margin:28px 0 12px;color:${T.text}">$1</h2>`)
    .replace(/^- (.+)$/gm, '<li style="margin:4px 0;color:#B0B8C9">$1</li>')
    .replace(/(<li.*<\/li>\n?)+/g, (m: string) => `<ul style="padding-left:20px;margin:8px 0">${m}</ul>`)
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" } as any,
  container: { maxWidth: 1200, margin: '0 auto', padding: '0 20px' } as any,
  header: { padding: '20px 0', borderBottom: `1px solid ${T.border}`, marginBottom: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as any,
  logo: { fontSize: 20, fontWeight: 700, color: T.text, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 } as any,
  logoAccent: { background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  breadcrumbs: { display: 'flex', gap: 8, fontSize: 13, color: T.textMuted, marginBottom: 24 } as any,
  bcLink: { color: T.primary, textDecoration: 'none', cursor: 'pointer' } as any,
  hero: { display: 'flex', alignItems: 'flex-start', gap: 24, marginBottom: 32, flexWrap: 'wrap' as const } as any,
  heroIcon: { width: 80, height: 80, borderRadius: 16, objectFit: 'cover' as const, background: T.border, flexShrink: 0 } as any,
  heroIconPlaceholder: { width: 80, height: 80, borderRadius: 16, background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: T.text, fontWeight: 700, flexShrink: 0 } as any,
  heroInfo: { flex: 1, minWidth: 200 } as any,
  heroName: { fontSize: 32, fontWeight: 800, margin: '0 0 8px', lineHeight: 1.2 } as any,
  heroTagline: { fontSize: 16, color: T.textSecondary, margin: '0 0 12px', lineHeight: 1.5 } as any,
  heroBadges: { display: 'flex', gap: 8, flexWrap: 'wrap' as const } as any,
  badge: (color: string) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: `${color}20`, color }),
  actionBar: { display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' as const } as any,
  ctaBtn: { padding: '12px 28px', fontSize: 15, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, color: T.text, transition: 'opacity 0.15s' } as any,
  linkBtn: { padding: '10px 20px', fontSize: 14, fontWeight: 500, borderRadius: 8, border: `1px solid ${T.bgActive}`, background: T.bgCard, color: T.textSecondary, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 } as any,
  tabs: { display: 'flex', gap: 0, borderBottom: `1px solid ${T.bgHover}`, marginBottom: 24 } as any,
  tab: (active: boolean) => ({
    padding: '12px 20px', fontSize: 14, fontWeight: active ? 600 : 400, cursor: 'pointer',
    color: active ? T.primary : T.textSecondary, borderBottom: active ? '2px solid #5B8DEF' : '2px solid transparent',
    background: 'transparent', border: 'none', transition: 'all 0.15s',
  }),
  layout: { display: 'flex', gap: 40 } as any,
  main: { flex: 1, minWidth: 0 } as any,
  sidebar: { width: 300, flexShrink: 0 } as any,
  content: { fontSize: 15, lineHeight: 1.7, color: T.textSecondary } as any,
  sidebarCard: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 20 } as any,
  sidebarTitle: { fontSize: 14, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: T.textSecondary } as any,
  relatedItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer', borderBottom: `1px solid ${T.bgCard}` } as any,
  relatedIcon: { width: 32, height: 32, borderRadius: 6, objectFit: 'cover' as const, background: T.border } as any,
  skeleton: { background: `linear-gradient(90deg, ${T.bgCard} 25%, T.border 50%, T.bgCard 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 8 } as any,
  footer: { padding: '40px 0', borderTop: `1px solid ${T.border}`, textAlign: 'center' as const, color: T.textMuted, fontSize: 13, marginTop: 40 } as any,
};

type TabKey = 'overview' | 'features' | 'installation' | 'requirements';

export function MarketplaceDetail({ locale: propLocale, slug }: { path?: string; locale?: string; slug?: string }) {
  const locale = propLocale || getLocale();
  const [entry, setEntry] = useState<MarketplaceEntry | null>(null);
  const [related, setRelated] = useState<RelatedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  useEffect(() => {
    if (!slug) return;
    fetch(`${API_BASE}/entries/${slug}?contentType=marketplace&locale=${locale}`)
      .then(r => r.json())
      .then(res => {
        const item = res?.data?.entry;
        setEntry(item || null);
        setRelated(res?.data?.related || []);
        if (item) {
          // Track view
          fetch(`${API_BASE}/entries/${item.id}/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_type: 'view', locale }),
          }).catch(() => {});
        }
      }).catch(() => {}).finally(() => setLoading(false));
  }, [slug, locale]);

  useEffect(() => {
    if (!entry) return;
    const meta = parseMeta(entry.metadata);
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: entry.title,
      description: entry.excerpt,
      applicationCategory: entry.category_name,
      softwareVersion: meta.version,
      license: meta.license,
      operatingSystem: 'Linux',
      url: meta.website_url || window.location.href,
    };
    let script = document.getElementById('app-ld-json') as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.id = 'app-ld-json';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    document.title = `${entry.title} - WhiteLabelClaw Marketplace`;
    return () => { script?.remove(); };
  }, [entry]);

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={{ padding: '80px 0' }}>
            <div style={{ ...S.skeleton, width: 80, height: 80, borderRadius: 16, marginBottom: 16 }} />
            <div style={{ ...S.skeleton, width: '40%', height: 32, marginBottom: 12 }} />
            <div style={{ ...S.skeleton, width: '60%', height: 16 }} />
          </div>
        </div>
        <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>
      </div>
    );
  }

  if (!entry) {
    return (
      <div style={S.page}>
        <div style={{ ...S.container, textAlign: 'center', padding: '100px 0' }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Item nao encontrado</h1>
          <a href={`/${locale}`} style={{ color: T.primary }}>Voltar ao marketplace</a>
        </div>
      </div>
    );
  }

  const meta = parseMeta(entry.metadata);
  const custom = parseCustom(entry.custom_fields || '{}');
  const iconUrl = meta.icon_url || '';
  const version = meta.version || '';
  const license = meta.license || '';
  const status = meta.status || 'stable';
  const websiteUrl = meta.website_url || '';
  const githubUrl = meta.github_url || '';
  const minPlan = meta.min_plan || 'free';
  const installCount = meta.install_count || 0;

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'overview', label: 'Visao Geral' },
    { key: 'features', label: 'Features' },
    { key: 'installation', label: 'Instalacao' },
    { key: 'requirements', label: 'Requisitos' },
  ];

  const tabContent: Record<TabKey, string> = {
    overview: entry.body || 'Sem descricao disponivel.',
    features: (() => { try { const f = JSON.parse(entry.features_json || '[]'); return Array.isArray(f) ? f.join('\n- ') : String(f); } catch { return entry.features_json || 'Sem features listadas.'; } })(),
    installation: custom.installation_guide || 'Guia de instalacao em breve.',
    requirements: custom.requirements || 'Sem requisitos especificos.',
  };

  return (
    <div style={S.page}>
      <div style={S.container}>
        {/* Header */}
        <div style={S.header}>
          <a href={`/${locale}`} style={S.logo}>
            <img src="/logo-white.png" alt="WhiteLabelClaw" width={28} height={28} style={{ borderRadius: 6 }} />
            <span>WhiteLabelClaw <span style={S.logoAccent}>Marketplace</span></span>
          </a>
        </div>

        {/* Breadcrumbs */}
        <div style={S.breadcrumbs}>
          <span style={S.bcLink} onClick={() => { window.location.href = `/${locale}`; }}>Marketplace</span>
          <span>/</span>
          {entry.category_name && <>
            <span style={S.bcLink} onClick={() => { window.location.href = `/${locale}?cat=${entry.category_slug}`; }}>{entry.category_name}</span>
            <span>/</span>
          </>}
          <span style={{ color: T.text }}>{entry.title}</span>
        </div>

        {/* Hero */}
        <div style={S.hero}>
          {iconUrl
            ? <img style={S.heroIcon} src={iconUrl} alt={entry.title} />
            : <div style={S.heroIconPlaceholder}>{(entry.title || '?')[0]}</div>}
          <div style={S.heroInfo}>
            <h1 style={S.heroName}>{entry.title}</h1>
            <p style={S.heroTagline}>{entry.excerpt}</p>
            <div style={S.heroBadges}>
              {entry.category_name && <span style={S.badge(entry.category_color || T.primary)}>{entry.category_name}</span>}
              <span style={S.badge(status === 'stable' ? T.success : status === 'beta' ? T.warning : T.textMuted)}>{status}</span>
              {version && <span style={S.badge(T.textMuted)}>v{version}</span>}
              {license && <span style={S.badge(T.textMuted)}>{license}</span>}
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div style={S.actionBar}>
          <Button variant="primary" size="lg" style={S.ctaBtn}>Instalar Agora</Button>
          {websiteUrl && <a href={websiteUrl} target="_blank" rel="noopener" style={S.linkBtn}>Website</a>}
          {githubUrl && <a href={githubUrl} target="_blank" rel="noopener" style={S.linkBtn}>GitHub</a>}
        </div>

        {/* Layout: main + sidebar */}
        <div style={S.layout}>
          <div style={S.main}>
            <div style={S.tabs}>
              {tabs.map(t => (
                <Button variant="ghost" key={t.key} style={S.tab(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>
                  {t.label}
                </Button>
              ))}
            </div>
            <div style={S.content} dangerouslySetInnerHTML={{ __html: renderSimpleMarkdown(tabContent[activeTab]) }} />
          </div>

          {/* Sidebar */}
          <div style={S.sidebar}>
            <div style={S.sidebarCard}>
              <div style={S.sidebarTitle}>Estatisticas</div>
              <div style={{ display: 'flex', gap: 20 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{entry.view_count || 0}</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>Views</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{installCount}</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>Installs</div>
                </div>
              </div>
            </div>

            {related.length > 0 && (
              <div style={S.sidebarCard}>
                <div style={S.sidebarTitle}>Relacionados</div>
                {related.map(r => {
                  const rMeta = parseMeta(r.metadata || '{}');
                  return (
                    <div key={r.id} style={S.relatedItem} onClick={() => { window.location.href = `/${locale}/${r.slug}`; }}>
                      {rMeta.icon_url
                        ? <img style={S.relatedIcon} src={rMeta.icon_url} alt={r.title} loading="lazy" />
                        : <div style={{ ...S.relatedIcon, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1a1e3a, #12162b)', fontSize: 14, color: T.bgSecondary }}>{(r.title || '?')[0]}</div>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{r.title}</div>
                        <div style={{ fontSize: 12, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.excerpt}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={S.footer}>
          <p>WhiteLabelClaw - Plataforma de automacao e deploy</p>
        </div>
      </div>
      <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>
    </div>
  );
}
